import type Pusher from 'pusher-js'

import type { PersonalizationEligibility } from '@b-stock/fe-core/bstock-next/hooks'
import type { StoreFrontOptionsDefaultViewEnum } from '@b-stock/listing-api-client'
import type { SavedSearchDto } from '@b-stock/saved-search-api-client'
import type {
  AllListingsSortOrderEnum,
  AllListingsSortByEnum,
} from '@b-stock/search-api-client'

import type {
  DateRange,
  DistanceRange,
  Range,
} from '@/components/listings/search/SearchFilters/shared/commonTypes'
import type { KeysMatchingOptional } from '@/helpers/types/KeysMatching'
import type {
  SearchPage,
  SearchParams,
  SearchResultsPage,
} from '@/queries/listingSearchQuery'
import type { SimilarSearchResult } from '@/queries/similarSearchQuery'

import type { SearchAction } from './stateActions'

export type ListingsSort = [AllListingsSortByEnum, AllListingsSortOrderEnum]

/**
 * Request params a page always sends on top of the buyer's own filters, e.g.
 * the First Dibs page limiting results to the buyer's early-access listings.
 * Never part of the search state, so the buyer can't clear them and they
 * don't show up in the URL or applied filters.
 */
export type ListingSearchScope = Pick<SearchParams['params'], 'isFirstDibs'>

// Watchlist "Closes In" bucket options. Buckets are nested, not partitioned —
// the lower bound is always "now" and only the upper bound changes: 5m ⊂ 30m
// ⊂ 1h ⊂ 24h.
export type ClosesInBucket = '5m' | '30m' | '1h' | '24h'

export const CLOSES_IN_BUCKET_MS: Record<ClosesInBucket, number> = {
  '5m': 5 * 60 * 1000,
  '30m': 30 * 60 * 1000,
  '1h': 60 * 60 * 1000,
  '24h': 24 * 60 * 60 * 1000,
}

// Guards against prototype-chain leakage — `value in CLOSES_IN_BUCKET_MS`
// would accept inherited property names ('toString', 'constructor', etc.),
// letting a crafted `?closesIn=toString` URL pass the whitelist. Downstream,
// `CLOSES_IN_BUCKET_MS['toString']` resolves to the inherited method (a
// function, not a number), so `now.getTime() + fn` yields NaN and
// `new Date(NaN).toISOString()` throws a RangeError that breaks the search.
export const isClosesInBucket = (value: unknown): value is ClosesInBucket =>
  typeof value === 'string' &&
  Object.prototype.hasOwnProperty.call(CLOSES_IN_BUCKET_MS, value)

// UI-facing bidStatus filter values. Defined here (not alongside the
// client-only useWatchlistBidStatusSync hook) so server code can compare
// against them at their actual runtime values — constants exported from a
// `'use client'` module cross the RSC boundary as opaque client references,
// not their literal strings.
export const BID_STATUS_WINNING = 'winning'
export const BID_STATUS_LOSING = 'losing'
export const BID_STATUS_NO_BID_YET = 'no_bid_yet'

export const LISTINGS_SORT_END_TIME: ListingsSort = ['endTime', 'asc']
export const LISTINGS_SORT_RELEVANCE: ListingsSort = ['relevance', 'desc']
export const LISTINGS_SORT_RECOMMENDED: ListingsSort = ['recommended', 'asc']
// 'personalization' is not yet in AllListingsSortByEnum — cast until search-api-client is updated (GLOB-4698)
export const LISTINGS_SORT_PERSONALIZED: ListingsSort = [
  'personalization' as AllListingsSortByEnum,
  'asc',
]

export type BooleanFilterKey = KeysMatchingOptional<
  ListingFiltersState,
  boolean
>

export type ListingFiltersState = {
  storefront: string[]
  /**
   * Clean storefront id(s), distinct from `storefront` (which holds an
   * opaque `"<id>-<display name>"` composite straight from the facet
   * response). Used by deep-links that already have a bare id (e.g. the
   * Auction Schedule grid's click-through) rather than needing to
   * reconstruct that composite string.
   */
  storeFrontId: string[]
  /**
   * Filtering on "top level" categories.
   * @see category for subcategory filtering
   */
  categories: string[]
  /**
   * Filtering on "subcategory" level categories
   * @see categories for top-level category filtering
   */
  category: string[]
  condition: string[]
  inventoryType: string[]
  productType: string[]
  manufacturer: string[]
  listingType: string[]
  buyingFormat: string[]
  transportMode: string[]
  allowedShipmentTypes: string[]
  region: string[]
  warehouseLocation: string[]
  model: string[]
  capacity: string[]
  memory: string[]
  carrier: string[]
  sellerGrade: string[]
  deals: string[]
  starterLots?: boolean
  newSeller?: boolean
  isCreditCardEnabled?: boolean
  eligibleOnly?: boolean
  /**
   * "My Bids" toggle. Unlike other boolean filters, this isn't sent to the
   * search API directly (see formatFilters) — it's translated client-side
   * into the `id` allowlist via useMyBidsListingIdSync, since bid data isn't
   * in the listings-search index.
   */
  myBids?: boolean
  /**
   * Watchlist bid-status filter (single-select). Like `myBids`, this isn't
   * sent to the search API — it's translated client-side into the `id`
   * allowlist via useWatchlistBidStatusSync (winning/losing intersected with
   * watchlist IDs, no_bid_yet computed as set-difference). Valid values:
   * 'winning' | 'losing' | 'no_bid_yet'. Undefined means "All" (no filter).
   */
  bidStatus?: string
  /**
   * Watchlist "Closes In" bucket (single-select). Stored as the bucket key,
   * NOT resolved timestamps — the translation to
   * `minEndTime`/`maxEndTime = [now, now + N]` happens at request time in
   * listingSearchStateToSearchParams so `now` is re-evaluated on every fetch
   * (pagination change, filter change, refresh). Storing the key also keeps
   * URLs stable across refreshes (?closesIn=5m). Undefined means "Any time".
   */
  closesIn?: ClosesInBucket
  currentBidRanges: Range
  msrp: Range
  msrpPercentage: number | null
  landedCostPercentage: number | null
  units: Range
  problemFreeOrderRate: number | null
  /**
   * Category composition threshold. When non-null and at
   * least one category is selected, the search API filters to listings where the
   * chosen category makes up at least this percentage of the lot.
   * Valid values: 5 | 10 | 25 | 50 | 100. Null in reducer state until the buyer
   * picks a value; the 10% default is derived in `effectiveState` (see the
   * ENABLE_CATEGORY_COMPOSITION_THRESHOLD flag JSDoc).
   */
  categoryThreshold: number | null
  distanceRange: DistanceRange
  endTimeRange: DateRange
  q: string | null
}

export type ListingSearchState = {
  page: number
  pageSize: number
  sortBy: AllListingsSortByEnum
  sortOrder: AllListingsSortOrderEnum
  view: StoreFrontOptionsDefaultViewEnum
  filters: ListingFiltersState
  appliedFromSavedSearch: boolean
  applyingSearchQuery: boolean
  id: string[] | null
  /**
   * "Freeze Sort" (GLOB-4832): pauses Pusher-driven re-ranking so row order
   * stays put while in-row data (bid, price, countdown) keeps updating live.
   * Never persisted (no URL param, no saved preference) — always resets to
   * false on refresh/navigation since it's initialized from getDefaultState.
   */
  isSortFrozen: boolean
}

/** Why the similar-inventory shelf is on, as reported to Segment. */
export type SimilarShelfReason = 'end_of_served_listings' | 'zero_results'

export type SimilarShelfPageState = {
  /** True while the search still holds a previous key's results. */
  isPlaceholderData: boolean
  total: number | undefined
  isLastPage: boolean
  isGridView: boolean
  hasFilters: boolean
  /** The My Bids toggle owns its own empty state, which is not a search result. */
  isMyBidsSurface: boolean
  /** The watchlist mounts this provider and renders no shelf. */
  isSearchSurface: boolean
}

export type SimilarShelfResolution = {
  eligibleForEndOfList: boolean
  eligibleForZeroResults: boolean
  /**
   * Eligibility narrowed to a settled search. For zero results the two
   * coincide, since that eligibility already requires one.
   */
  exposeEndOfList: boolean
  exposeZeroResults: boolean
  reason: SimilarShelfReason
}

export type SimilarSearchState = {
  /** Trigger conditions met: an eligible placement plus its flag's treatment. */
  enabled: boolean
  data: SimilarSearchResult | undefined
  isFetching: boolean
  /**
   * Which placement turned the shelf on. Optional so the limited contexts that
   * hand-build this object (carousels, MoreFromSeller) stay untouched; the
   * shelf falls back to the end-of-list reason.
   */
  reasonShown?: SimilarShelfReason
}

export type ListingSearchContextValue = {
  isLoading: boolean
  state: ListingSearchState
  dispatch: (action: SearchAction) => void
  setPage: (page: number) => void
  setPageSize: (pageSize: number) => void
  setSavedSearch: (id: SavedSearchDto['_id']) => void
  setSort: (
    sortBy: AllListingsSortByEnum,
    sortOrder: AllListingsSortOrderEnum
  ) => void
  setView: (view: StoreFrontOptionsDefaultViewEnum) => void
  setSortFrozen: (isSortFrozen: boolean) => void
  /**
   * Freeze Sort analytics (GLOB-4832/BP-2422): call when a bid is confirmed
   * while sort is frozen, so the `freeze_sort_session` event's
   * `bids_placed_while_frozen` reflects engagement during the freeze. Threaded
   * explicitly through the bid-confirmation flow rather than called directly
   * from it (useListingSearch throws outside this provider, and that flow is
   * also used on the LDP, which has no ListingSearchProvider ancestor).
   */
  recordBidPlacedWhileFrozen: () => void
  getActiveFilterCount: () => number
  updateFilters: (filters: Partial<ListingFiltersState>) => void
  resetFilters: () => void
  data: SearchResultsPage | null
  error?: Error | null
  updateSearchData: (updater: <T extends SearchPage>(data: T) => T) => void
  pusherClient3MP?: Pusher
  pusherClientEnt?: Pusher
  currentImpressionId: string | null
  similarSearch: SimilarSearchState
  /** Loading/error state for the buyer's active-bid IDs fetch behind the `myBids` toggle. */
  myBidsFilter: { isLoading: boolean; isError: boolean }
  personalizationEligibility: PersonalizationEligibility
  rankingSurface?: string
  /** Params every search on this page carries; see `ListingSearchScope`. */
  scope?: ListingSearchScope
  /**
   * The buyer's explicit category threshold from reducer state, before
   * `state` derives the default or strips it for flag-off buyers. The URL is
   * written from this so it keeps only explicit values and survives the flag
   * resolving after first render. Only set by ListingSearchProvider.
   */
  rawCategoryThreshold?: number | null
  /**
   * True when this surface opted in and the buyer is in the remembered
   * page-size cohort. Only set by ListingSearchProvider.
   */
  isPageSizeRemembered?: boolean
}

export const SUPPORTED_QUERY_PARAMS = [
  'q',
  'p',
  's',
  'o',
  'id',
  'l',
  'view',
  'newSeller',
] as const
export type SupportedQueryParam = (typeof SUPPORTED_QUERY_PARAMS)[number]

export type QueryParams = {
  [key: string]: string | string[] | undefined
}
